import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  FileCheck,
  Search,
  Download,
  RefreshCw,
  CheckCircle2,
  XCircle,
  PenTool,
  Camera,
  MapPin,
  User,
  Calendar,
  Eye } from
'lucide-react';

const API_URL = API_BASE;

const ProofTrail = () => {const { t } = useTranslation("wms");
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPod, setSelectedPod] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    routeId: '',
    dateRange: '24h'
  });

  useEffect(() => {
    fetchPods();
  }, [filters.dateRange]);

  const fetchPods = async () => {
    setLoading(true);
    // Use mock data for demo
    setPods(getMockPods());
    setLoading(false);
  };

  const getMockPods = () => [
  {
    id: 'POD-001',
    shipment_id: 'SHP-2026-001',
    stop_id: 'STOP-001',
    route_id: 'RT-2026-001',
    package_ids: ['PKG-001', 'PKG-002', 'PKG-003'],
    pod_level: 'SIGNATURE_PHOTO',
    delivery_status: 'DELIVERED',
    has_signature: true,
    has_photo: true,
    recipient_name: 'John Smith',
    otp_verified: false,
    geo: { lat: -23.5505, lng: -46.6333 },
    captured_by: 'driver@ezerp.com',
    captured_at: new Date(Date.now() - 600000).toISOString(),
    signature_data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwAAACoCAMAAABt9SM...',
    photo_data: null
  },
  {
    id: 'POD-002',
    shipment_id: 'SHP-2026-002',
    stop_id: 'STOP-002',
    route_id: 'RT-2026-001',
    package_ids: ['PKG-004'],
    pod_level: 'BASIC',
    delivery_status: 'FAILED',
    has_signature: false,
    has_photo: true,
    recipient_name: null,
    failure_reason: 'CUSTOMER_ABSENT',
    failure_detail: 'Customer not home after 2 call attempts',
    geo: { lat: -23.5510, lng: -46.6340 },
    captured_by: 'driver@ezerp.com',
    captured_at: new Date(Date.now() - 1200000).toISOString()
  },
  {
    id: 'POD-003',
    shipment_id: 'SHP-2026-003',
    stop_id: 'STOP-003',
    route_id: 'RT-2026-001',
    package_ids: ['PKG-005', 'PKG-006'],
    pod_level: 'SIGNATURE_PHOTO_OTP',
    delivery_status: 'DELIVERED',
    has_signature: true,
    has_photo: true,
    recipient_name: 'Maria Santos',
    otp_verified: true,
    geo: { lat: -23.5520, lng: -46.6350 },
    captured_by: 'driver@ezerp.com',
    captured_at: new Date(Date.now() - 1800000).toISOString()
  },
  {
    id: 'POD-004',
    shipment_id: 'SHP-2026-004',
    stop_id: 'STOP-004',
    route_id: 'RT-2026-002',
    package_ids: ['PKG-007'],
    pod_level: 'SIGNATURE',
    delivery_status: 'PARTIAL',
    has_signature: true,
    has_photo: false,
    recipient_name: 'Carlos Lima',
    geo: { lat: -23.5530, lng: -46.6360 },
    captured_by: 'driver2@ezerp.com',
    captured_at: new Date(Date.now() - 3600000).toISOString()
  }];


  const getStatusBadge = (status) => {
    switch (status) {
      case 'DELIVERED':
        return <Badge className="bg-green-500 gap-1"><CheckCircle2 className="h-3 w-3" />{t("delivered", "Delivered")}</Badge>;
      case 'FAILED':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{t("failed", "Failed")}</Badge>;
      case 'PARTIAL':
        return <Badge className="bg-amber-500">{t("partial", "Partial")}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
  };

  const handleSearch = () => {
    const filtered = getMockPods().filter((pod) => {
      if (filters.status !== 'all' && pod.delivery_status !== filters.status) return false;
      if (filters.routeId && !pod.route_id.includes(filters.routeId)) return false;
      return true;
    });
    setPods(filtered);
  };

  // Stats
  const stats = {
    total: pods.length,
    delivered: pods.filter((p) => p.delivery_status === 'DELIVERED').length,
    failed: pods.filter((p) => p.delivery_status === 'FAILED').length,
    withSignature: pods.filter((p) => p.has_signature).length,
    withPhoto: pods.filter((p) => p.has_photo).length
  };

  return (
    <div className="p-6 space-y-6" data-testid="proof-trail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
            <FileCheck className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("proofTrail", "Proof Trail")}</h1>
            <p className="text-muted-foreground">{t("podSignaturesPhotos", "POD signatures, photos, GPS stamps")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchPods}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">{t("totalPods", "Total PODs")}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
            <p className="text-sm text-green-600">{t("delivered", "Delivered")}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
            <p className="text-sm text-red-600">{t("failed", "Failed")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.withSignature}</p>
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <PenTool className="h-3 w-3" />{t("signatures", "Signatures")}

            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.withPhoto}</p>
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
              <Camera className="h-3 w-3" />{t("photos", "Photos")}

            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("status", "Status")}</label>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}>
                
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses", "All Statuses")}</SelectItem>
                  <SelectItem value="DELIVERED">{t("delivered", "Delivered")}</SelectItem>
                  <SelectItem value="FAILED">{t("failed", "Failed")}</SelectItem>
                  <SelectItem value="PARTIAL">{t("partial", "Partial")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("routeId", "Route ID")}</label>
              <Input
                placeholder="RT-001..."
                value={filters.routeId}
                onChange={(e) => setFilters((prev) => ({ ...prev, routeId: e.target.value }))} />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("period", "Period")}</label>
              <Select
                value={filters.dateRange}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, dateRange: v }))}>
                
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t("last24h", "Last 24h")}</SelectItem>
                  <SelectItem value="7d">{t("last7Days", "Last 7 days")}</SelectItem>
                  <SelectItem value="30d">{t("last30Days", "Last 30 days")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />{t("search", "Search")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* POD Cards */}
      <div className="grid grid-cols-2 gap-4">
        {loading ?
        <div className="col-span-2 text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">{t("loadingPods", "Loading PODs...")}</p>
          </div> :
        pods.length === 0 ?
        <div className="col-span-2 text-center py-8 text-muted-foreground">{t("noPodsFound", "No PODs found")}

        </div> :

        pods.map((pod) =>
        <Card
          key={pod.id}
          className="cursor-pointer hover:shadow-md transition-all"
          onClick={() => setSelectedPod(pod)}>
          
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{pod.shipment_id}</h3>
                      {getStatusBadge(pod.delivery_status)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{t("stop", "Stop:")}
                  {pod.stop_id}{t("route", "\u2022 Route:")}{pod.route_id}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>

                {/* POD Artifacts */}
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant={pod.has_signature ? 'default' : 'outline'} className="gap-1">
                    <PenTool className="h-3 w-3" />{t("signature", "Signature")}

              </Badge>
                  <Badge variant={pod.has_photo ? 'default' : 'outline'} className="gap-1">
                    <Camera className="h-3 w-3" />{t("photo", "Photo")}

              </Badge>
                  {pod.otp_verified &&
              <Badge className="bg-green-500 gap-1">
                      <CheckCircle2 className="h-3 w-3" />{t("otp", "OTP")}

              </Badge>
              }
                </div>

                {/* Details */}
                <div className="text-sm space-y-1">
                  {pod.recipient_name &&
              <p className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-3 w-3" />
                      {pod.recipient_name}
                    </p>
              }
                  {pod.failure_reason &&
              <p className="flex items-center gap-2 text-red-600">
                      <XCircle className="h-3 w-3" />
                      {pod.failure_reason}
                    </p>
              }
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {formatTime(pod.captured_at)}
                  </p>
                  {pod.geo &&
              <p className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {(pod.geo?.lat ?? 0).toFixed(4)}, {(pod.geo?.lng ?? 0).toFixed(4)}
                    </p>
              }
                </div>

                {/* Package Count */}
                <div className="mt-3 pt-3 border-t">
                  <Badge variant="outline" className="text-xs">
                    {pod.package_ids?.length || 0}{t("packages", "packages")}
              </Badge>
                </div>
              </CardContent>
            </Card>
        )
        }
      </div>

      {/* POD Detail Modal */}
      {selectedPod &&
      <PODDetailModal pod={selectedPod} onClose={() => setSelectedPod(null)} />
      }
    </div>);

};

// POD Detail Modal Component
const PODDetailModal = ({ pod, onClose }) => {const { t } = useTranslation("wms");
  return (
    <Dialog open={!!pod} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />{t("podDetails", "POD Details -")}
            {pod.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">{t("deliveryStatus", "Delivery Status")}</p>
              <p className="text-lg font-medium">{pod.delivery_status}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{t("podLevel", "POD Level")}</p>
              <Badge variant="secondary">{pod.pod_level}</Badge>
            </div>
          </div>

          {/* Recipient */}
          {pod.recipient_name &&
          <div className="p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">{t("recipient", "Recipient")}</p>
              <p className="font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                {pod.recipient_name}
              </p>
            </div>
          }

          {/* Failure Info */}
          {pod.failure_reason &&
          <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-950/20 rounded-lg">
              <p className="text-sm text-red-600 mb-1">{t("failureReason", "Failure Reason")}</p>
              <p className="font-medium text-red-700">{pod.failure_reason}</p>
              {pod.failure_detail &&
            <p className="text-sm text-red-600 mt-1">{pod.failure_detail}</p>
            }
            </div>
          }

          {/* Artifacts */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <PenTool className="h-4 w-4" />{t("signature", "Signature")}

              </p>
              {pod.has_signature ?
              <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
                  {pod.signature_data ?
                <img src={pod.signature_data} alt="Signature" className="max-h-full" /> :

                <p className="text-sm text-green-600">{t("captured", "\u2713 Captured")}</p>
                }
                </div> :

              <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">{t("notCaptured", "Not captured")}</p>
                </div>
              }
            </div>
            <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <Camera className="h-4 w-4" />{t("photo", "Photo")}

              </p>
              {pod.has_photo ?
              <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
                  {pod.photo_data ?
                <img src={pod.photo_data} alt="Delivery" className="max-h-full rounded" /> :

                <p className="text-sm text-green-600">{t("captured", "\u2713 Captured")}</p>
                }
                </div> :

              <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">{t("notCaptured", "Not captured")}</p>
                </div>
              }
            </div>
          </div>

          {/* Location */}
          {pod.geo &&
          <div className="p-4 border rounded-lg">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" />{t("gpsLocation", "GPS Location")}

            </p>
              <p className="text-sm text-muted-foreground">
                {pod.geo.lat}, {pod.geo.lng}
              </p>
            </div>
          }

          {/* Meta */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">{t("capturedBy", "Captured By")}</p>
              <p className="font-medium">{pod.captured_by}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("capturedAt", "Captured At")}</p>
              <p className="font-medium">{new Date(pod.captured_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("packages", "Packages")}</p>
              <p className="font-medium">{pod.package_ids?.join(', ')}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

};

export default ProofTrail;